import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../../api/client';
import {
  certDisplayStatus,
  certStatusLabel,
  fileToDataUrl,
  formatDisplayDate,
  initialsFromName,
  isValidPhone,
  type TeamCertification,
  type TeamEmergencyContact,
  type TeamMemberDetail
} from './teamTypes';

type TeamMemberDrawerProps = {
  memberId: string;
  member: TeamMemberDetail | null;
  loading: boolean;
  onClose: () => void;
  onUpdated: (member: TeamMemberDetail) => void;
};

type EditDraft = {
  name: string;
  phone: string;
  jobTitle: string;
  department: string;
  bio: string;
  avatarBase64: string;
  hiredAt: string;
  payrollEmployeeCode: string;
  certifications: TeamCertification[];
  emergencyContact: TeamEmergencyContact;
};

const emptyEmergency: TeamEmergencyContact = { name: '', phone: '', relationship: '' };

function toDraft(member: TeamMemberDetail): EditDraft {
  return {
    name: member.name || '',
    phone: member.phone || '',
    jobTitle: member.jobTitle || '',
    department: member.department || '',
    bio: member.bio || '',
    avatarBase64: member.avatarBase64 || '',
    hiredAt: member.hiredAt ? member.hiredAt.slice(0, 10) : '',
    payrollEmployeeCode: member.payrollEmployeeCode || '',
    certifications: (member.certifications || []).map((c) => ({ ...c })),
    emergencyContact: { ...emptyEmergency, ...(member.emergencyContact || {}) }
  };
}

function certRowClass(status: ReturnType<typeof certDisplayStatus>): string {
  switch (status) {
    case 'expiring':
      return 'team-cert-status team-cert-status--expiring';
    case 'expired':
      return 'team-cert-status team-cert-status--expired';
    case 'valid':
      return 'team-cert-status team-cert-status--valid';
    default:
      return 'team-cert-status team-cert-status--none';
  }
}

export default function TeamMemberDrawer({
  memberId,
  member,
  loading,
  onClose,
  onUpdated
}: TeamMemberDrawerProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [showAddCert, setShowAddCert] = useState(false);
  const [newCert, setNewCert] = useState<TeamCertification>({ name: '', issuedBy: '', expiresAt: '', notes: '' });
  const [deleteCertIndex, setDeleteCertIndex] = useState<number | null>(null);

  useEffect(() => {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setShowAddCert(false);
    setDeleteCertIndex(null);
  }, [memberId, member?._id]);

  const display = useMemo(() => {
    if (editing && draft) {
      return {
        ...member,
        name: draft.name,
        phone: draft.phone,
        jobTitle: draft.jobTitle,
        department: draft.department,
        bio: draft.bio,
        avatarBase64: draft.avatarBase64,
        hiredAt: draft.hiredAt || null,
        payrollEmployeeCode: draft.payrollEmployeeCode,
        certifications: draft.certifications,
        emergencyContact: draft.emergencyContact
      } as TeamMemberDetail;
    }
    return member;
  }, [member, editing, draft]);

  const startEdit = () => {
    if (!member) return;
    setDraft(toDraft(member));
    setEditing(true);
    setSaveError(null);
    setAvatarError(null);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setShowAddCert(false);
    setDeleteCertIndex(null);
    setAvatarError(null);
  };

  const handleStatusChange = async (nextStatus: 'active' | 'inactive') => {
    if (!member || statusSaving) return;
    setStatusSaving(true);
    setSaveError(null);
    try {
      const res = await apiRequest<{ user: TeamMemberDetail }>(`/api/team/${memberId}/status`, {
        method: 'PATCH',
        json: { status: nextStatus }
      });
      onUpdated(res.user);
    } catch {
      setSaveError('Failed to update status. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      setSaveError('Name is required.');
      return;
    }
    if (!isValidPhone(draft.phone)) {
      setSaveError('Phone must be a valid phone number.');
      return;
    }
    if (draft.bio.length > 300) {
      setSaveError('Bio must be 300 characters or fewer.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiRequest<{ user: TeamMemberDetail }>(`/api/team/${memberId}`, {
        method: 'PATCH',
        json: {
          name: draft.name.trim(),
          phone: draft.phone.trim(),
          jobTitle: draft.jobTitle.trim(),
          department: draft.department.trim(),
          bio: draft.bio.trim(),
          avatarBase64: draft.avatarBase64 || null,
          hiredAt: draft.hiredAt || null,
          payrollEmployeeCode: draft.payrollEmployeeCode.trim(),
          certifications: draft.certifications.map((c) => ({
            name: c.name,
            issuedBy: c.issuedBy || '',
            expiresAt: c.expiresAt || null,
            notes: c.notes || ''
          })),
          emergencyContact: draft.emergencyContact
        }
      });
      onUpdated(res.user);
      setEditing(false);
      setDraft(null);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = async (file: File) => {
    setAvatarError(null);
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      setAvatarError('Avatar must be a PNG or JPG image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('Avatar must be 2 MB or smaller.');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setDraft((prev) => (prev ? { ...prev, avatarBase64: dataUrl } : prev));
    } catch {
      setAvatarError('Failed to read image.');
    }
  };

  const addCertification = () => {
    if (!draft || !newCert.name.trim()) return;
    setDraft({
      ...draft,
      certifications: [
        ...draft.certifications,
        {
          name: newCert.name.trim(),
          issuedBy: newCert.issuedBy?.trim() || '',
          expiresAt: newCert.expiresAt || null,
          notes: newCert.notes?.trim() || ''
        }
      ]
    });
    setNewCert({ name: '', issuedBy: '', expiresAt: '', notes: '' });
    setShowAddCert(false);
  };

  const removeCertification = (index: number) => {
    if (!draft) return;
    setDraft({
      ...draft,
      certifications: draft.certifications.filter((_, i) => i !== index)
    });
    setDeleteCertIndex(null);
  };

  const emergency = display?.emergencyContact;
  const hasEmergency =
    Boolean(emergency?.name?.trim()) ||
    Boolean(emergency?.phone?.trim()) ||
    Boolean(emergency?.relationship?.trim());

  return (
    <div className="team-drawer-root" role="presentation">
      <button type="button" className="team-drawer-backdrop" aria-label="Close panel" onClick={onClose} />
      <aside className="team-drawer-panel" role="dialog" aria-modal="true" aria-label="Employee details">
        <div className="team-drawer-toolbar">
          <div className="team-drawer-toolbar-actions">
            {!editing ? (
              <button type="button" className="btn-secondary" onClick={startEdit} disabled={loading || !member}>
                Edit
              </button>
            ) : (
              <>
                <button type="button" className="btn-primary" onClick={() => void handleSave()} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn-secondary" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </button>
              </>
            )}
            <button type="button" className="team-drawer-close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {saveError ? <div className="banner banner-error team-drawer-banner">{saveError}</div> : null}
        {avatarError ? <div className="banner banner-error team-drawer-banner">{avatarError}</div> : null}

        {loading || !display ? (
          <div className="team-drawer-loading">Loading employee…</div>
        ) : (
          <div className="team-drawer-body">
            <section className="team-drawer-section team-drawer-header-section">
              <div className="team-drawer-header-row">
                {editing ? (
                  <label className="team-drawer-avatar-edit">
                    {display.avatarBase64 ? (
                      <img src={display.avatarBase64} alt="" className="team-drawer-avatar-lg" />
                    ) : (
                      <div className="team-drawer-avatar-lg team-drawer-avatar-lg--initials">
                        {initialsFromName(display.name)}
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleAvatarPick(file);
                      }}
                    />
                    <span>Change photo</span>
                  </label>
                ) : display.avatarBase64 ? (
                  <img src={display.avatarBase64} alt="" className="team-drawer-avatar-lg" />
                ) : (
                  <div className="team-drawer-avatar-lg team-drawer-avatar-lg--initials">
                    {initialsFromName(display.name)}
                  </div>
                )}
                <div className="team-drawer-header-meta">
                  {editing && draft ? (
                    <input
                      className="form-input"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                  ) : (
                    <h2 className="team-drawer-name">{display.name}</h2>
                  )}
                  {editing && draft ? (
                    <input
                      className="form-input"
                      placeholder="Job title"
                      value={draft.jobTitle}
                      onChange={(e) => setDraft({ ...draft, jobTitle: e.target.value })}
                    />
                  ) : (
                    <p className="team-drawer-subtitle">{display.jobTitle || 'Team member'}</p>
                  )}
                  <span
                    className={
                      display.role === 'admin'
                        ? 'team-role-badge team-role-badge--admin'
                        : 'team-role-badge team-role-badge--technician'
                    }
                  >
                    {display.role === 'admin' ? 'Admin' : 'Technician'}
                  </span>
                </div>
              </div>
              <div className="team-status-toggle">
                <span className="form-label">Status</span>
                <div className="team-status-toggle-row">
                  <button
                    type="button"
                    className={`team-status-pill${display.status === 'active' ? ' is-active' : ''}`}
                    disabled={statusSaving || display.status === 'active'}
                    onClick={() => void handleStatusChange('active')}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    className={`team-status-pill${display.status === 'inactive' ? ' is-active' : ''}`}
                    disabled={statusSaving || display.status === 'inactive'}
                    onClick={() => void handleStatusChange('inactive')}
                  >
                    Inactive
                  </button>
                </div>
              </div>
            </section>

            <section className="team-drawer-section">
              <h3 className="team-drawer-section-title">Contact</h3>
              <div className="team-drawer-field">
                <span className="form-label">Email</span>
                <p className="team-drawer-readonly">{display.email}</p>
                <p className="settings-field-helper">Managed by account settings</p>
              </div>
              <div className="team-drawer-field">
                <span className="form-label">Phone</span>
                {editing && draft ? (
                  <input
                    className="form-input"
                    value={draft.phone}
                    onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  />
                ) : (
                  <p className="team-drawer-readonly">{display.phone?.trim() || '—'}</p>
                )}
              </div>
            </section>

            <section className="team-drawer-section">
              <h3 className="team-drawer-section-title">Work</h3>
              <div className="team-drawer-field">
                <span className="form-label">Department</span>
                {editing && draft ? (
                  <input
                    className="form-input"
                    value={draft.department}
                    onChange={(e) => setDraft({ ...draft, department: e.target.value })}
                  />
                ) : (
                  <p className="team-drawer-readonly">{display.department?.trim() || '—'}</p>
                )}
              </div>
              <div className="team-drawer-field">
                <span className="form-label">Hired date</span>
                {editing && draft ? (
                  <input
                    className="form-input"
                    type="date"
                    value={draft.hiredAt}
                    onChange={(e) => setDraft({ ...draft, hiredAt: e.target.value })}
                  />
                ) : (
                  <p className="team-drawer-readonly">{formatDisplayDate(display.hiredAt)}</p>
                )}
              </div>
              {display?.role === 'technician' ? (
                <div className="team-drawer-field">
                  <span className="form-label">Payroll employee ID</span>
                  {editing && draft ? (
                    <>
                      <input
                        className="form-input"
                        placeholder="e.g. employee ID or payroll code"
                        value={draft.payrollEmployeeCode}
                        onChange={(e) => setDraft({ ...draft, payrollEmployeeCode: e.target.value })}
                      />
                      <p className="settings-field-helper">
                        Used in payroll CSV export and live sync. Leave blank to use a short internal code.
                      </p>
                    </>
                  ) : (
                    <p className="team-drawer-readonly">{display.payrollEmployeeCode?.trim() || '—'}</p>
                  )}
                </div>
              ) : null}
              <div className="team-drawer-field">
                <span className="form-label">Bio / Notes</span>
                {editing && draft ? (
                  <>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      maxLength={300}
                      value={draft.bio}
                      onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
                    />
                    <p className="settings-field-helper">{draft.bio.length}/300</p>
                  </>
                ) : (
                  <p className="team-drawer-readonly">{display.bio?.trim() || '—'}</p>
                )}
              </div>
            </section>

            <section className="team-drawer-section">
              <h3 className="team-drawer-section-title">Certifications</h3>
              {(editing && draft ? draft.certifications : display.certifications || []).length === 0 ? (
                <p className="settings-placeholder-text">No certifications on file.</p>
              ) : (
                <ul className="team-cert-list">
                  {(editing && draft ? draft.certifications : display.certifications || []).map((cert, index) => {
                    const status = certDisplayStatus(cert.expiresAt);
                    return (
                      <li key={`${cert.name}-${index}`} className="team-cert-list-row">
                        <div className="team-cert-list-main">
                          <strong>{cert.name}</strong>
                          <span>{cert.issuedBy || '—'}</span>
                          <span>Expires {formatDisplayDate(cert.expiresAt)}</span>
                          <span className={certRowClass(status)}>{certStatusLabel(status)}</span>
                        </div>
                        {editing ? (
                          <div className="team-cert-list-actions">
                            {deleteCertIndex === index ? (
                              <div className="settings-inline-confirm">
                                <p className="settings-inline-confirm-text">Remove this certification?</p>
                                <div className="settings-inline-confirm-actions">
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => removeCertification(index)}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    className="settings-text-btn"
                                    onClick={() => setDeleteCertIndex(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="settings-text-btn settings-text-btn--danger"
                                onClick={() => setDeleteCertIndex(index)}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
              {editing && draft ? (
                <>
                  {!showAddCert ? (
                    <button type="button" className="btn-secondary settings-inline-action" onClick={() => setShowAddCert(true)}>
                      Add Certification
                    </button>
                  ) : (
                    <div className="team-add-cert-form">
                      <input
                        className="form-input"
                        placeholder="Cert name"
                        value={newCert.name}
                        onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                      />
                      <input
                        className="form-input"
                        placeholder="Issued by"
                        value={newCert.issuedBy || ''}
                        onChange={(e) => setNewCert({ ...newCert, issuedBy: e.target.value })}
                      />
                      <input
                        className="form-input"
                        type="date"
                        value={newCert.expiresAt || ''}
                        onChange={(e) => setNewCert({ ...newCert, expiresAt: e.target.value })}
                      />
                      <input
                        className="form-input"
                        placeholder="Notes (optional)"
                        value={newCert.notes || ''}
                        onChange={(e) => setNewCert({ ...newCert, notes: e.target.value })}
                      />
                      <div className="settings-inline-confirm-actions">
                        <button type="button" className="btn-primary" onClick={addCertification}>
                          Add
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setShowAddCert(false)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </section>

            <section className="team-drawer-section">
              <h3 className="team-drawer-section-title">Emergency Contact</h3>
              {editing && draft ? (
                <div className="team-drawer-field-grid">
                  <input
                    className="form-input"
                    placeholder="Name"
                    value={draft.emergencyContact.name || ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        emergencyContact: { ...draft.emergencyContact, name: e.target.value }
                      })
                    }
                  />
                  <input
                    className="form-input"
                    placeholder="Phone"
                    value={draft.emergencyContact.phone || ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        emergencyContact: { ...draft.emergencyContact, phone: e.target.value }
                      })
                    }
                  />
                  <input
                    className="form-input"
                    placeholder="Relationship"
                    value={draft.emergencyContact.relationship || ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        emergencyContact: { ...draft.emergencyContact, relationship: e.target.value }
                      })
                    }
                  />
                </div>
              ) : hasEmergency ? (
                <div className="team-drawer-field-grid team-drawer-readonly-grid">
                  <p>
                    <span className="form-label">Name</span>
                    {emergency?.name || '—'}
                  </p>
                  <p>
                    <span className="form-label">Phone</span>
                    {emergency?.phone || '—'}
                  </p>
                  <p>
                    <span className="form-label">Relationship</span>
                    {emergency?.relationship || '—'}
                  </p>
                </div>
              ) : (
                <p className="settings-placeholder-text">No emergency contact on file.</p>
              )}
            </section>

            <section className="team-drawer-section">
              <h3 className="team-drawer-section-title">Account</h3>
              <div className="team-account-grid">
                <p>
                  <span className="form-label">Login email</span>
                  {display.email}
                </p>
                <p>
                  <span className="form-label">Account created</span>
                  {formatDisplayDate(display.createdAt)}
                </p>
                <p>
                  <span className="form-label">Last login</span>
                  {display.lastLogin ? formatDisplayDate(display.lastLogin) : 'Never'}
                </p>
                <p>
                  <span className="form-label">Google connected</span>
                  {display.googleConnected ? 'Yes' : 'No'}
                </p>
                {display.mustChangePassword ? (
                  <p className="team-password-required">Password change required</p>
                ) : null}
              </div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
